<script setup lang="ts">
import { onClickOutside } from '@vueuse/core';

// Barra de cima do jogo em fullscreen (mobile). Desenho: Figma node
// 22444:51166 — frame de 393 × 58, duas linhas (30 e 19).
// Layout: [‹ Voltar] · [logo] · [Depositar ⌄] / [faixa de nível].
const { openDepositModal, openWithdrawModal } = usePayments();
const { userCanOnlyWithdraw } = useUserLimits();
const { isAuthenticated } = storeToRefs(useAuthStore());
const casinoStore = useCasinoStore();
const { updateWallet } = useWallet();
const { lastPreviewGame, reopenLastPreview } = useGameMobilePreview();
const { isClubFidelidadeEnabled } = useFeatureFlagsJson();
// Inbox da Smartico, o mesmo do header do site (components/user/header/
// Buttons.vue): `dp:inbox` pela store, não pelo `window._smartico` na mão.
// `runAction` aqui embaixo já é o nome do despachante do menu desta barra — daí
// o apelido.
const { runAction: openSmarticoInbox } = useSmarticoInit();
const { unReadMessages } = storeToRefs(useSmarticoMessages());

// A bolinha só existe com mensagem por ler; o NÚMERO dentro dela depende da
// mesma flag que manda no contador do header (`isNotificationActived`, lida
// junto da gamificação lá embaixo) — com ela desligada fica só o ponto
// vermelho.
const hasUnreadMessages = computed(() => unReadMessages.value > 0);

const unReadMessagesCount = computed(() =>
  unReadMessages.value > 9 ? '9+' : unReadMessages.value,
);

const loyaltyHubStore = useLoyaltyHubStore();
const router = useRouter();

// Pílula de nível no lugar do logo: o jogador vê o XP subindo enquanto joga.
// Só entra com a gamificação de níveis ligada e usuário logado — sem isso o
// logo continua como sempre foi.
const { isGamificationLevelsActived, isNotificationActived } =
  storeToRefs(useSmarticoConfigs());

const { isReadyGamefication } = storeToRefs(useSmarticoInit());

const showLevelProgress = computed(
  () =>
    isAuthenticated.value &&
    isGamificationLevelsActived.value &&
    isReadyGamefication.value,
);

// ENG-817: quem decide isso é o GameIframe (lê `launchWalletType`, a única
// fonte) e publica no `casino` store — ver components/casino/game/
// GameIframe.vue. Bar.vue só lê.
const isPlayingWithBonus = computed(() => casinoStore.isPlayingWithBonus);

// Valor real do rollover (apostado vs. meta, em R$) pra faixa que substitui a
// de XP — mesma fonte que a carteira usa (usePlayWithBonus), sem recalcular
// a fórmula aqui.
const { rolloverAmount } = usePlayWithBonus();

// Lockup do Figma 22526:60653: emblema + "APOSTA" / "TUDO" empilhados, numa
// caixa baixinha de 22px. É o `logo-vertical-white.webp` (276 × 70) que o site
// já serve — não dá pra usar o BaseCommonLogo, que monta o logo horizontal
// (proporção 6,8) e nesta altura ficaria com ~150px, comendo os botões.
const logoUrl = computed(() =>
  getAssetsUrlForCDN('/assets/images/logo-vertical-white.webp'),
);

// A barra tem TRÊS alturas: 42px no layout de sempre (voltar · logo · ações),
// 83px quando a faixa de NÍVEL entra na segunda linha — 5 + 34 + 6 + 34 + 3 —
// e 72px quando é a faixa de ROLLOVER — 5 + 34 + 6 + 24 + 3. Os 24 (contra os
// 34 da faixa de nível) já dão um respiro pequeno mas visível em volta da
// chapa compacta de uma linha só (15,77px) sem exagerar — 90px tinha respiro
// grande demais pra quem não tem chips pra preencher.
// Os 34 da faixa de nível são 14 a mais que os 69 do frame do Figma
// (22624:72182): o degrau dos chips cresceu pra caber a pílula dentro da
// chapa, o respiro de cima subiu de 2 pra 5 porque no celular a primeira linha
// ficava grudada na moldura da tela, e o vão entre as duas linhas foi de 3 pra
// 6 pra placa da faixa não encostar no logo. A faixa tem que caber INTEIRA
// aqui, senão o que sobra é justamente a fileira de chips, cortada na quina
// do jogo.
//
// Quem desconta a altura do iframe é o GameIframe, e ele lê `--games-bar-h` —
// então a troca tem que acontecer na variável, não num valor local, senão as
// duas pontas saem de sincronia e o topo do jogo some atrás da barra.
const BAR_H = { plain: '42px', levels: '83px', rollover: '72px' };

watchEffect(() => {
  if (!import.meta.client) return;

  let barHeight = BAR_H.plain;

  if (casinoStore.isGameModalOpen) {
    if (isPlayingWithBonus.value && rolloverAmount.value.isActive) {
      barHeight = BAR_H.rollover;
    } else if (showLevelProgress.value) {
      barHeight = BAR_H.levels;
    }
  }

  document.documentElement.style.setProperty('--games-bar-h', barHeight);
});

onUnmounted(() => {
  // Devolve o valor do :root; sem isso a altura da barra do jogo continuaria
  // valendo depois que a barra saiu da tela.
  document.documentElement.style.removeProperty('--games-bar-h');
});

// A pílula de ação carrega um MENU: o chevron abre a bandeja logo abaixo, com
// barriguinha apontando pro botão — é onde moram saque, Clube e níveis, que em
// fullscreen não têm outro caminho.
const actionsRef = ref<HTMLElement | null>(null);
const isActionsMenuOpen = ref(false);

onClickOutside(actionsRef, () => {
  isActionsMenuOpen.value = false;
});

function runAction(action: () => void) {
  isActionsMenuOpen.value = false;
  action();
}

// Clube Fidelidade na bandeja: mesmo gatilho da badge da barra de menu
// (store.open(), que manda pro login se deslogado). O hub é um overlay do
// layout em z-index 9990 e esta barra vive em 99999 — ela precisa sair de cena
// enquanto o hub estiver aberto, senão fica flutuando por cima do painel.
const showLoyaltyHubAction = computed(
  () => isClubFidelidadeEnabled.value && isAuthenticated.value,
);

// Níveis mora numa PÁGINA (/vip/levels), o mesmo destino do menu da conta —
// sair do fullscreen é o efeito esperado, e quem derruba a barra é o
// `onBeforeRouteLeave` lá embaixo.
function goToLevels() {
  navigateTo(localeLink('/vip/levels'));
}

// Fecha junto com a barra: sem isso a bandeja reaparecia aberta na volta.
watch(
  () => casinoStore.isGameModalOpen,
  (open) => {
    if (!open) isActionsMenuOpen.value = false;
  },
);

type Emits = {
  'full-screen-off': [];
};

const emit = defineEmits<Emits>();

function backHome() {
  emit('full-screen-off');
  casinoStore.isGameModalOpen = false;
  updateWallet();

  // Veio do MobilePreview: volta pra rota anterior e reabre o preview
  // no jogo que estava sendo visualizado. Quando não há histórico (entrada
  // direta via deeplink ?direct=true), navega pra home pra que o preview
  // tenha onde renderizar como overlay sobre o conteúdo principal.
  if (lastPreviewGame.value) {
    const hasHistory = !!window.history.state?.back;

    if (hasHistory) {
      router.back();
    } else {
      router.push('/');
    }

    reopenLastPreview();
  }
}

onBeforeRouteLeave(() => {
  casinoStore.isGameModalOpen = false;
});
</script>

<template>
  <div
    class="gamesBar"
    :class="{
      hidden: !casinoStore.isGameModalOpen || loyaltyHubStore.isOpen,
      hasLevels: showLevelProgress,
    }"
  >
    <div class="menu">
      <button
        class="back"
        type="button"
        @click="backHome"
      >
        <NuxtIcon
          class="backIcon"
          name="arrows/chevron_left"
        />
        {{ $t('casino.game.go_back') }}
      </button>

      <!-- Logo no vão do meio: item `flex-1` do space-between, então ele
           SEMPRE cabe entre o Voltar e as ações, em qualquer largura de tela
           (a versão absoluta centrada atropelava os botões nas telas
           estreitas). -->
      <div class="relative z-[1] mx-2 flex min-w-0 flex-1 justify-center">
        <NuxtLink
          :aria-label="appName()"
          class="logo"
          no-prefetch
          :to="localeLink('/')"
        >
          <img
            :alt="appName()"
            :src="logoUrl"
          />
        </NuxtLink>
      </div>

      <div
        ref="actionsRef"
        class="actions"
      >
        <!-- A pílula é só a moldura: a pastilha laranja leva direto pro depósito
             e QUEM ABRE a bandeja é o chevron — clicar na caixa inteira dava
             dois destinos pro mesmo toque. -->
        <div class="btn-deposit">
          <button
            v-if="!userCanOnlyWithdraw"
            class="btn-deposit__label"
            type="button"
            @click="openDepositModal()"
          >
            {{ $t('header.deposit') }}
          </button>

          <!-- Sino do Figma 22616:71934: em fullscreen o header do site sai de
               cena, e com ele o único caminho pro inbox da Smartico. Mesma
               ação de lá (`dp:inbox`) — só a moldura é desta barra. Fica fora
               do `v-if` do saldo porque mensagem chega pra quem só pode
               sacar também. -->
          <button
            v-if="isAuthenticated"
            :aria-label="$t('notifications.title')"
            class="btn-deposit__bell"
            type="button"
            @click="openSmarticoInbox('dp:inbox')"
          >
            <NuxtIcon
              class="bellIcon"
              name="game-bar/bell"
            />

            <span
              v-if="hasUnreadMessages"
              class="btn-deposit__badge"
            >
              <template v-if="isNotificationActived">
                {{ unReadMessagesCount }}
              </template>
            </span>
          </button>

          <button
            :aria-expanded="isActionsMenuOpen"
            aria-haspopup="menu"
            :aria-label="$t('header.deposit')"
            class="btn-deposit__toggle"
            type="button"
            @click="isActionsMenuOpen = !isActionsMenuOpen"
          >
            <NuxtIcon
              class="depositIcon"
              :class="{ isOpen: isActionsMenuOpen }"
              filled
              name="game-bar/chevron-toggle"
            />
          </button>
        </div>

        <div
          v-if="isActionsMenuOpen"
          class="actionsMenu"
          role="menu"
        >
          <!-- Saque não existe pra quem não está logado (o openWithdrawModal
               retorna sem fazer nada), então o item nem entra. -->
          <button
            v-if="isAuthenticated"
            role="menuitem"
            type="button"
            @click="runAction(openWithdrawModal)"
          >
            <NuxtIcon
              class="itemIcon is-atm"
              name="game-bar/atm"
            />
            {{ $t('casino.game.withdraw_balance') }}
          </button>

          <!-- Mensagens (22616:71940): o MESMO destino do sino da pílula
               (`dp:inbox`). O sino continua sendo o atalho de um toque, e este
               item é o caminho nomeado pra quem abre a bandeja — foi assim que
               o desenho fechou. Sem a bolinha de não-lidas: quem carrega o
               contador é o sino. -->
          <button
            v-if="isAuthenticated"
            role="menuitem"
            type="button"
            @click="runAction(() => openSmarticoInbox('dp:inbox'))"
          >
            <span class="itemIconBox">
              <NuxtIcon
                class="itemIcon is-envelope"
                name="game-bar/envelope"
              />

              <!-- Mesma fonte do sino (`unReadMessages` + a flag do contador):
                   ou os dois mostram o número, ou os dois ficam só no ponto. -->
              <span
                v-if="hasUnreadMessages"
                class="itemBadge"
              >
                <template v-if="isNotificationActived">
                  {{ unReadMessagesCount }}
                </template>
              </span>
            </span>
            {{ $t('casino.game.messages') }}
          </button>

          <!-- Atalhos que em jogo não têm outro caminho: a barra de menu (com a
               badge do Clube) e o menu da conta ficam fora do fullscreen.
               Depositar NÃO entra aqui — é o rótulo da própria pílula. -->
          <button
            v-if="showLoyaltyHubAction"
            role="menuitem"
            type="button"
            @click="runAction(loyaltyHubStore.open)"
          >
            <NuxtIcon
              class="itemIcon is-handshake"
              name="game-bar/handshake"
            />
            {{ $t('casino.game.loyalty_hub') }}
          </button>

          <button
            v-if="isAuthenticated"
            role="menuitem"
            type="button"
            @click="runAction(goToLevels)"
          >
            <NuxtIcon
              class="itemIcon"
              name="game-bar/level-badge"
            />
            {{ $t('casino.game.levels') }}
          </button>
        </div>
      </div>
    </div>

    <!-- Segunda linha, largura cheia: é o que tira o nível do aperto de
         137px do meio da barra e deixa a barra de XP atravessar a tela. -->
    <ClientOnly>
      <LevelsInGameRollover
        v-if="isPlayingWithBonus && rolloverAmount.isActive"
        :amount-bet="rolloverAmount.betAmount"
        :amount-target="rolloverAmount.targetAmount"
        class="levelRow"
      />
      <LevelsInGameProgress
        v-else-if="showLevelProgress"
        class="levelRow"
      />
    </ClientOnly>
  </div>
</template>

<style scoped lang="scss">
.gamesBar {
  @apply w-full fixed top-0 left-0 z-[99999];

  // Publicada em :root (main.scss) porque o GameIframe desconta esta altura do
  // iframe — as duas pontas leem o mesmo valor.
  @apply flex flex-col;

  height: var(--games-bar-h);
  // Figma 22526:60646: fundo CHAPADO. A cor do elo saiu do fundo da barra e
  // ficou só no preenchimento do XP.
  background: #030a12;
  padding: 5px 4px 7px;
  gap: 2px;
  // Sem borda embaixo: o Figma fecha a barra chapada contra o jogo.
}

// Com a faixa de nível o desenho (22624:72182) é outro frame: 3 de padding
// lateral, 3 de respiro entre as duas linhas e as linhas em 34 + 30 — os 69px
// da barra.
.gamesBar.hasLevels {
  // 5 em cima e não os 4 do Figma (nem os 2 que tínhamos): no celular a barra
  // encosta na moldura da tela e a primeira linha parecia colada nela.
  // 5 nas laterais e não os 3 do desenho: o conteúdo quase encostava nas duas
  // bordas da tela.
  padding: 5px 5px 3px;
  // 6 de respiro entre as duas linhas contra os 3 do desenho: o logo tem pouca
  // margem própria e a placa da faixa é uma chapa clara — a 3 as duas
  // encostavam, a 10 a barra já parecia solta do header.
  gap: 6px;
}

// Primeira linha: voltar · logo · depositar. Altura fixa pra que a faixa de
// nível fique com o resto — sem ela o `space-between` dos dois blocos brigava
// pela mesma linha e espremia os dois.
.menu {
  @apply flex flex-row items-center justify-between w-full shrink-0;

  height: 30px;
}

// A pílula de ações cresceu (o sino entrou), e com ela a linha: 34 no desenho
// novo. Só no layout COM faixa de nível — sem ela a barra continua sendo os
// 42px de sempre.
.gamesBar.hasLevels .menu {
  height: 34px;
}

// Segunda linha: come o que sobra da altura da barra.
.levelRow {
  @apply min-h-0 flex-1;
}

/* ---------- voltar ---------- */
// Pílula do Figma 22526:60649 ("Tabs", 67 × 28): o traço SAIU — agora é só o
// fundo `flat/default-flat` (zinc/700 a 40%) com raio 12. Dentro, a caixa de
// 54 × 23 com chevron 17 + rótulo, e 6/12 de padding.
.back {
  @apply flex items-center justify-center shrink-0;

  width: 67px;
  height: 28px;
  padding: 0 12px 0 6px;
  border-radius: 12px;
  background: rgba(63, 63, 70, 0.4);
  color: #f1f5f9;
  font-size: 12px;
  font-weight: 700;
  line-height: 16px;
  -webkit-tap-highlight-color: transparent;
}

// `chevron_left` e não o `angle_left` que estava aqui: o desenho pede um chevron
// FINO de 7,33 × 12,22 (proporção 0,60), e é essa a proporção do viewBox do
// chevron (17 × 28) — o angle_left é atarracado (0,90). O ícone é `1em`, com o
// traço limitado pela ALTURA, então `font-size` = a altura desejada.
.backIcon {
  @apply inline-flex items-center justify-center shrink-0;

  // Caixa de 17px do desenho (22526:60651), com o traço medindo 6,38 × 11,16
  // dentro dela (os insets do chevron). Como o glifo é limitado pela ALTURA,
  // `font-size` = a altura do traço; a caixa de 17px é quem dá a folga até o
  // rótulo — no Figma o gap entre os dois é ZERO, a respiração é essa.
  width: 17px;
  height: 17px;
  font-size: 11.16px;

  :deep(svg) {
    // O arquivo declara `width: 1em`, mas o traço só ocupa 17/28 = 0,607em da
    // caixa e fica centrado nela — sobrava ~2,4px de vazio de cada lado, que
    // empurravam o chevron pra dentro e inflavam o gap até "Voltar". Com a
    // largura na proporção do viewBox a caixa abraça o traço.
    width: 0.607em;
    // O NuxtIcon injeta um <svg> com margin-bottom próprio; zera pra alinhar o
    // chevron com a linha do texto.
    margin-bottom: 0;
  }
}

/* ---------- logo ---------- */
.logo {
  @apply flex items-center shrink-0;

  img {
    @apply block w-auto;

    // Figma 22526:60653: o lockup empilhado numa caixa de 22px de altura. A
    // largura vem da proporção do arquivo (276 × 70 → ~87px).
    height: 22px;
  }
}

/* ---------- depositar ---------- */
.actions {
  // `lg:hidden` mantém o que já valia aqui: no desktop (onde o fullscreen é
  // caminho de exceção e o header do site continua à mão) a barra fica só com
  // voltar + logo.
  @apply flex items-center shrink-0 lg:hidden relative;

  // A bandeja desce POR CIMA da faixa de nível, e a faixa ganhou uma placa
  // opaca posicionada. Sem z-index os dois ficam em `auto` no mesmo contexto e
  // quem pinta por último vence — a faixa, que vem depois no DOM. Daí a bandeja
  // sumindo atrás dela. O 3 é pra passar também do logo, que já era z-1.
  z-index: 3;
}

// Bandeja do Figma 22563:62588 ("DROP-GAME", 125 × 110): painel chapado
// #0D1423 SEM borda, raio 5, 10 de padding e 5 de respiro entre os itens.
// `animation` e não `transition`: o main.scss zera transition no app inteiro.
.actionsMenu {
  @apply absolute right-0 flex flex-col;

  top: calc(100% + 7px);
  width: 125px;
  padding: 10px;
  gap: 5px;
  border-radius: 5px;
  background: #0d1423;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.55);
  animation: actionsMenuIn 120ms ease-out;

  // O rabicho (nó "Union") é um quadrado de cantos arredondados girado 45°: a
  // diagonal de 15,07 do desenho sai de um lado de 10,66. Sem borda, então ele
  // é só a mesma cor do painel — não precisa emendar traço com nada.
  &::before {
    @apply absolute block;

    content: '';
    top: -3.14px;
    right: 6.09px;
    width: 10.66px;
    height: 10.66px;
    border-radius: 3px;
    background: #0d1423;
    transform: rotate(45deg);
  }

  // Item: a MESMA caixa flat do resto da barra (`flat/default-flat`), sem traço
  // e sem cor própria — as pastilhas coloridas da versão anterior saíram do
  // desenho. Texto 12/12 SemiBold com o tracking apertado.
  button {
    @apply flex items-center w-full whitespace-nowrap text-left;

    gap: 8px;
    height: 27px;
    padding: 0 12px;
    border-radius: 6px;
    background: rgba(63, 63, 70, 0.4);
    color: #fff;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: -0.24px;
    line-height: 12px;
    -webkit-tap-highlight-color: transparent;

    &:active {
      filter: brightness(1.4);
    }
  }
}

// Os quatro ícones moram numa caixa de 13 (é o que casa o começo dos rótulos),
// e o `font-size` é quem dita o tamanho porque o nuxt-icons impõe
// `svg { width: 1em; height: 1em }`. O do saque é menor no desenho (9,77) e o
// aperto de mão tem viewBox quadrado só pra não esticar, então cada um tem a
// sua medida.
.itemIcon {
  @apply inline-flex items-center justify-center shrink-0;

  width: 13px;
  height: 13px;
  font-size: 13px;

  &.is-atm {
    font-size: 9.7666px;
  }

  &.is-handshake {
    font-size: 12.2353px;
  }

  // O envelope é o único DEITADO (10,03 × 8,06 no desenho). O nuxt-icons manda
  // `width: 1em; height: 1em` no svg, o que esmagaria o desenho num quadrado —
  // então o `font-size` vale a ALTURA e a largura vem da proporção (1,2444).
  &.is-envelope {
    font-size: 8.0602px;

    :deep(svg) {
      width: 1.2444em;
    }
  }

  :deep(svg) {
    margin-bottom: 0;
  }
}

// Só o item de Mensagens precisa disso: a caixa de 13 do ícone vira a âncora da
// bolinha de não-lidas. Mesma medida dos outros ícones, então o começo dos
// rótulos não sai do lugar.
.itemIconBox {
  @apply relative inline-flex items-center justify-center shrink-0;

  width: 13px;
  height: 13px;
}

// A MESMA bolinha do sino (22616:71938), só que ancorada na quina do envelope —
// aqui a caixa é de 13px, não os 22,8 do disco do sino.
.itemBadge {
  @apply absolute flex items-center justify-center;

  top: -1px;
  left: 8.5px;
  min-width: 6.165px;
  height: 6.165px;
  padding: 0 1px;
  border-radius: 999px;
  background: #ff383c;
  color: #fff;
  font-size: 4.816px;
  font-weight: 800;
  letter-spacing: -0.0963px;
  line-height: 1;
}

@keyframes actionsMenuIn {
  from {
    opacity: 0;
    transform: translateY(-4px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

// Figma 22563:63383 (111 × 30): a pílula agora é a MESMA caixa flat do Voltar —
// só o fundo `flat/default-flat` e raio 12, sem traço. O que era o "+" laranja
// virou uma PASTILHA laranja com o rótulo dentro, mais o chevron do lado.
.btn-deposit {
  @apply flex items-center justify-center whitespace-nowrap;

  // 7 e não os 4 do desenho: com o sino no meio, os três elementos ficavam
  // grudados — a pastilha laranja quase encostava no disco do sino.
  gap: 7px;
  height: 30px;
  // Largura pelo CONTEÚDO e não os 140 do desenho: o sino é opcional (só entra
  // logado) e o "Depositar" some pra quem só pode sacar — numa largura fixa a
  // pílula ficaria com um vão do tamanho do que faltou. O padding é o que casa
  // com o desenho quando os três estão lá.
  padding: 0 8px;
  border-radius: 12px;
  background: rgba(63, 63, 70, 0.4);
  -webkit-tap-highlight-color: transparent;
}

.gamesBar.hasLevels .btn-deposit {
  height: 34px;
}

// A pastilha laranja (22563:63434, 74 × 26): é ela que carrega a cor de ação
// agora, e não o ícone.
.btn-deposit__label {
  @apply flex items-center justify-center shrink-0;

  width: 74px;
  height: 26px;
  border-radius: 8px;
  background: #ff3700;
  color: #e2e8f0;
  font-size: 12px;
  font-weight: 700;
  line-height: 16px;
  -webkit-tap-highlight-color: transparent;

  &:active {
    filter: brightness(0.94);
  }
}

// Sino (22563:63388 reaproveitado como moldura, 22,848): disco de branco 5%,
// que é o que o separa da pílula sem precisar de traço.
.btn-deposit__bell {
  @apply relative flex items-center justify-center shrink-0;

  width: 22.848px;
  height: 22.848px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.05);
  -webkit-tap-highlight-color: transparent;

  &:active {
    filter: brightness(1.4);
  }
}

// O sino ocupa 13,28 da caixa de 22,8 — e como o nuxt-icons manda no tamanho
// pelo `font-size`, é ele quem carrega a medida.
.bellIcon {
  @apply inline-flex items-center justify-center shrink-0;

  width: 13.2847px;
  height: 13.2847px;
  font-size: 13.2847px;
  color: #fff;

  :deep(svg) {
    margin-bottom: 0;
  }
}

// Bolinha de não-lidas (22616:71938): 6,165 no desenho, com o número dentro.
// `min-width` em vez de largura fixa porque o contador chega a "9+" — em 6px
// fixos o segundo caractere sairia cortado.
.btn-deposit__badge {
  @apply absolute flex items-center justify-center;

  top: 2.34px;
  left: 15.26px;
  min-width: 6.165px;
  height: 6.165px;
  padding: 0 1px;
  border-radius: 999px;
  background: #ff383c;
  color: #fff;
  font-size: 4.816px;
  font-weight: 800;
  letter-spacing: -0.0963px;
  line-height: 1;
}

.btn-deposit__toggle {
  @apply flex items-center justify-center shrink-0;

  -webkit-tap-highlight-color: transparent;

  &:active {
    filter: brightness(0.94);
  }
}

// Chevron do Figma 22563:63388 ("Close", 22,848): o arquivo já traz o disco de
// branco 5% junto do traço, por isso entra com `filled` — sem ele o
// `fill: currentColor` do nuxt-icons pintaria o disco inteiro de branco.
// O desenho mostra o menu ABERTO, com o chevron pra cima; fechado ele aponta
// pra baixo, que é o convite pra abrir. `animation` e não `transition`: o
// main.scss zera transition no app inteiro.
.depositIcon {
  @apply inline-flex items-center justify-center shrink-0;

  width: 22.848px;
  height: 22.848px;
  font-size: 22.848px;
  transform: rotate(180deg);

  &.isOpen {
    transform: rotate(0deg);
    animation: depositIconFlip 140ms ease-out;
  }

  :deep(svg) {
    margin-bottom: 0;
  }
}

@keyframes depositIconFlip {
  from {
    transform: rotate(180deg);
  }

  to {
    transform: rotate(0deg);
  }
}
</style>
